<template>
    <div class="login-container">
        <div class="login-bg">
            <img src="@/assets/img/login-bg2.jpg" alt="找回密码背景" class="login-bg-img" />
        </div>

        <div class="login-card-wrapper">
            <div class="login-card">
                <h2 class="login-title">找回密码</h2>

                <form class="login-form" @submit.prevent="handleResetPwd">
                    <!-- 全局错误提示 -->
                    <div
                            v-if="globalError"
                            class="login-global-error"
                            :class="{ 'login-error-shake': shakeTrigger }"
                            :key="shakeTrigger"
                    >
                        <span>{{ globalError }}</span>
                    </div>

                    <!-- 邮箱输入框 -->
                    <div class="login-form-item">
                        <label class="login-form-label">邮箱</label>
                        <div class="login-input-wrapper">
                            <input
                                    type="text"
                                    v-model="form.email"
                                    placeholder="请输入邮箱"
                                    class="login-form-input"
                                    maxlength="100"
                                    ref="emailInput"
                                    @input="handleInput('email')"
                                    @blur="validateField('email')"
                                    @focus="showTooltip($event, errorText.email)"
                            />
                            <transition name="login-tooltip-fade">
                                <div v-if="fieldError.email" class="login-error-tooltip">
                                    <span class="login-tooltip-text">{{ errorText.email }}</span>
                                    <div class="login-tooltip-arrow"></div>
                                </div>
                            </transition>
                        </div>
                    </div>

                    <!-- 验证码区域 -->
                    <div class="login-form-item">
                        <label class="login-form-label">验证码</label>
                        <div class="login-code-container">
                            <div class="login-input-wrapper login-code-input-wrapper">
                                <input
                                        type="text"
                                        v-model="form.code"
                                        class="login-form-input login-code-input"
                                        :title="errorText.code"
                                        maxlength="6"
                                        placeholder="请输入6位验证码"
                                        ref="codeInput"
                                        @input="handleInput('code')"
                                        @blur="validateField('code')"
                                        @focus="showTooltip($event, errorText.code)"
                                />
                                <transition name="login-tooltip-fade">
                                    <div v-if="fieldError.code && form.code.trim()" class="login-error-tooltip login-code-tooltip">
                                        <span class="login-tooltip-text">{{ errorText.code }}</span>
                                        <div class="login-tooltip-arrow"></div>
                                    </div>
                                </transition>
                            </div>
                            <button
                                    type="button"
                                    class="login-get-code-btn"
                                    :disabled="isCodeDisabled || !isEmailValid"
                                    @click="getVerificationCode"
                            >
                                {{ codeText }}
                            </button>
                        </div>
                    </div>

                    <!-- 新密码输入框 -->
                    <div class="login-form-item">
                        <label class="login-form-label">新密码</label>
                        <div class="login-input-wrapper">
                            <input
                                    type="password"
                                    v-model="form.newPwd"
                                    class="login-form-input"
                                    :title="errorText.newPwd"
                                    maxlength="20"
                                    placeholder="请输入新密码（6-20位）"
                                    ref="newPwdInput"
                                    @input="handleInput('newPwd')"
                                    @blur="validateField('newPwd')"
                                    @focus="showTooltip($event, errorText.newPwd)"
                            />
                            <transition name="login-tooltip-fade">
                                <div v-if="fieldError.newPwd && form.newPwd.trim()" class="login-error-tooltip">
                                    <span class="login-tooltip-text">{{ errorText.newPwd }}</span>
                                    <div class="login-tooltip-arrow"></div>
                                </div>
                            </transition>
                        </div>
                    </div>

                    <!-- 确认密码输入框 -->
                    <div class="login-form-item">
                        <label class="login-form-label">确认新密码</label>
                        <div class="login-input-wrapper">
                            <input
                                    type="password"
                                    v-model="form.confirmPwd"
                                    class="login-form-input"
                                    :title="errorText.confirmPwd"
                                    maxlength="20"
                                    placeholder="请确认新密码"
                                    ref="confirmPwdInput"
                                    @input="handleInput('confirmPwd')"
                                    @blur="validateField('confirmPwd')"
                                    @focus="showTooltip($event, errorText.confirmPwd)"
                            />
                            <transition name="login-tooltip-fade">
                                <div v-if="fieldError.confirmPwd && form.confirmPwd.trim()" class="login-error-tooltip">
                                    <span class="login-tooltip-text">{{ errorText.confirmPwd }}</span>
                                    <div class="login-tooltip-arrow"></div>
                                </div>
                            </transition>
                        </div>
                    </div>

                    <!-- 重置密码按钮 -->
                    <div class="login-form-actions">
                        <button
                                type="submit"
                                class="login-submit-btn"
                                :disabled="isLoading || !isAllValid"
                        >
                            <span v-if="!isLoading">重置密码</span>
                            <span v-if="isLoading" class="login-loading-spinner">⟳</span>
                        </button>
                    </div>

                    <!-- 登录链接 -->
                    <div class="login-links">
                        <button type="button" class="login-link-btn " @click="goToRegister">注册</button>
                        <button type="button" class="login-link-btn" @click="goToLogin">登录</button>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script setup>
    import { useFindPwdLogic } from './findPwd.js'

    // 引入并使用拆分的逻辑
    const {
        form,
        emailInput,
        codeInput,
        newPwdInput,
        confirmPwdInput,
        fieldError,
        errorText,
        globalError,
        isLoading,
        shakeTrigger,
        isEmailValid,
        codeText,
        isCodeDisabled,
        isAllValid,
        handleInput,
        validateField,
        getVerificationCode,
        handleResetPwd,
        goToRegister,
        goToLogin,
        showTooltip
    } = useFindPwdLogic()
</script>

<style scoped>
    @import '../../assets/css/login.css';
</style>